import React, { useState } from "react";
import { GraduationCap, Loader2 } from "lucide-react";
import CalculatorShell from "@/components/tools/CalculatorShell";
import { NumberInput, SliderInput } from "@/components/tools/Inputs";
import ResultsCard from "@/components/tools/ResultsCard";
import { useProducts } from "@/hooks/useProducts";
import { postTool } from "@/lib/api";
import { formatUSD, buildApplyQuery } from "@/lib/format";

export default function CollegeSavings() {
  const { products } = useProducts();
  const [form, setForm] = useState({
    child_age: 5,
    college_start_age: 18,
    annual_cost_today: 35000,
    years_of_college: 4,
    education_inflation: 0.05,
    monthly_savings: 400,
    crediting_rate: 0.06,
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (form.child_age < 0 || form.child_age > 22) e.child_age = "Use 0–22.";
    if (form.college_start_age <= form.child_age)
      e.college_start_age = "Must be after current child age.";
    if (form.annual_cost_today < 0) e.annual_cost_today = "Must be 0 or more.";
    if (form.years_of_college < 1 || form.years_of_college > 8)
      e.years_of_college = "Use 1–8 years.";
    if (form.monthly_savings < 0) e.monthly_savings = "Must be 0 or more.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    setServerError("");
    setResult(null);
    try {
      await new Promise((r) => setTimeout(r, 500));
      const data = await postTool("college-savings", form);
      setResult(data);
    } catch {
      setServerError("Could not calculate. Please check your inputs.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <CalculatorShell
      title="College Savings (IUL) Calculator"
      blurb="Forecast tomorrow's tuition and see if your plan will get you there."
      duration="Takes ~60 seconds"
      Icon={GraduationCap}
    >
      <form
        onSubmit={onSubmit}
        data-testid="college-form"
        className="bg-white rounded-2xl border border-lw-navy/10 p-6 sm:p-8 space-y-6"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <NumberInput
            label="Child's current age"
            value={form.child_age}
            onChange={set("child_age")}
            min={0}
            max={22}
            testId="college-child-age"
            error={errors.child_age}
          />
          <NumberInput
            label="College start age"
            value={form.college_start_age}
            onChange={set("college_start_age")}
            min={14}
            max={25}
            testId="college-start-age"
            error={errors.college_start_age}
          />
          <NumberInput
            label="Annual cost today"
            value={form.annual_cost_today}
            onChange={set("annual_cost_today")}
            prefix="$"
            min={0}
            testId="college-cost"
            helper="National avg: ~$35,000 (mix of public/private)."
            error={errors.annual_cost_today}
          />
          <NumberInput
            label="Years of college"
            value={form.years_of_college}
            onChange={set("years_of_college")}
            min={1}
            max={8}
            testId="college-years"
            error={errors.years_of_college}
          />
          <NumberInput
            label="Monthly amount you can save"
            value={form.monthly_savings}
            onChange={set("monthly_savings")}
            prefix="$"
            min={0}
            testId="college-monthly"
            error={errors.monthly_savings}
          />
        </div>

        <SliderInput
          label="Education inflation"
          value={form.education_inflation}
          onChange={set("education_inflation")}
          min={0}
          max={0.10}
          step={0.005}
          formatValue={(v) => `${(v * 100).toFixed(1)}%`}
          testId="college-inflation"
        />

        <SliderInput
          label="Expected IUL crediting rate"
          value={form.crediting_rate}
          onChange={set("crediting_rate")}
          min={0.04}
          max={0.08}
          step={0.005}
          formatValue={(v) => `${(v * 100).toFixed(1)}%`}
          testId="college-rate"
        />

        {serverError && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            {serverError}
          </p>
        )}

        <button
          type="submit"
          disabled={loading}
          data-testid="college-submit"
          className="btn-gold w-full sm:w-auto disabled:opacity-60"
        >
          {loading ? (
            <>
              <Loader2 className="animate-spin" size={18} /> Calculating…
            </>
          ) : (
            <>Project college costs →</>
          )}
        </button>
      </form>

      {result && (
        <ResultsCard
          source="college-savings"
          headline={`College will cost ~${formatUSD(result.total_college_cost)} in ${result.years_until_college} years`}
          subhead={`Your current savings rate projects ~${formatUSD(result.projected_savings)}. Gap: ${formatUSD(result.funding_gap)}.`}
          rows={[
            { label: "Years until college", value: `${result.years_until_college} yrs`, testId: "years-until" },
            { label: "Annual cost at start", value: formatUSD(result.future_annual_cost), testId: "annual-cost" },
            { label: "Total college cost", value: formatUSD(result.total_college_cost), testId: "total-cost" },
            { label: "Projected savings", value: formatUSD(result.projected_savings), testId: "projected" },
            { label: "Funding gap", value: formatUSD(result.funding_gap), testId: "gap" },
          ]}
          assumptions={result.assumptions}
          matchedIds={result.matched_product_ids}
          products={products}
          applyQuery={buildApplyQuery({
            source: "college-savings",
            gap: result.funding_gap,
            child_age: form.child_age,
            products: (result.matched_product_ids || []).join(","),
          })}
          toolSlug="college-savings"
          toolInputs={form}
          toolResults={result}
        />
      )}
    </CalculatorShell>
  );
}
